import { ClipboardList, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateQuiz,
	useDeleteQuiz,
	useQuizList,
	useUpdateQuiz,
} from '@/api/queries/direcionada/administration/quiz-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	type Branch,
	type QuestionInput,
	type Questionnaire,
	QuestionnaireFormContent,
} from '@/components/administration'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { QuizKind } from '@/types/quiz'
import { getApiErrorMessage } from '@/utils/api-error'

/* ────────────────────────── Main Page ────────────────────────────── */

export const QuestionnairesPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 500)

	const params = {
		...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
		page: 1,
		limit: 100,
	}

	const { data, isLoading } = useQuizList(params)
	const createMutation = useCreateQuiz()
	const updateMutation = useUpdateQuiz()
	const deleteMutation = useDeleteQuiz()

	// fetch organization data to populate branch selects
	const orgQuery = useOrganization()
	const branchOptions: Branch[] =
		orgQuery.data?.map((b) => ({ id: b.id, name: b.name })) ?? []

	const quizzes =
		data?.items.map((q, index) => ({
			id: q.id,
			number: String(index + 1).padStart(2, '0'),
			title: q.title,
			kind: q.kind,
			score: q.score,
			questions: q.questions.map((qn) => ({
				id: qn.id,
				question: qn.question,
				imagePath: qn.imagePath,
			})),
			branches: q.quizBranches.map((b) => b.id),
		})) || []

	// Form dialog/drawer state
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<Questionnaire | undefined>(undefined)
	const [deletingItem, setDeletingItem] = useState<{
		id: string
		title: string
	} | null>(null)

	/* ── Filtering ── */
	const filtered = quizzes.filter((q) => {
		const s = search.toLowerCase()
		return (
			q.title.toLowerCase().includes(s) ||
			q.number.includes(s) ||
			q.questions.some((question) => question)
		)
	})

	/* ── CRUD handlers ── */
	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (q: Questionnaire) => {
		setEditing(q)
		setFormOpen(true)
	}

	const handleSave = async ({
		title,
		kind,
		score,
		questions,
		branches,
	}: {
		title: string
		kind: QuizKind
		score?: number
		questions: QuestionInput[]
		branches: string[]
	}) => {
		try {
			if (editing) {
				await updateMutation.mutateAsync({
					id: editing.id,
					data: {
						title,
						score,
						questions,
						branchesId: branches,
					},
				})
				toast.success(t('administration.questionnaires.editSuccess', { title }))
			} else {
				await createMutation.mutateAsync({
					title,
					kind,
					score,
					questions, // Map to API format
					branchesId: branches,
				})
				toast.success(t('administration.questionnairesForm.createSuccess'))
			}

			setFormOpen(false)
			setEditing(undefined)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleDelete = async (id: string, title: string) => {
		try {
			await deleteMutation.mutateAsync(id)
			toast.success(t('administration.questionnaires.deleteSuccess', { title }))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleConfirmDelete = async () => {
		if (!deletingItem) {
			return
		}

		await handleDelete(deletingItem.id, deletingItem.title)
		setDeletingItem(null)
	}

	/* ── Responsive form ── */
	const formTitle = editing
		? t('administration.questionnaires.editQuestionnaire')
		: t('administration.questionnaires.createNew')

	return (
		<>
			<AdminCardPage
				icon={<ClipboardList className="size-5" />}
				title={t('administration.questionnaires.title')}
				subtitle={t('administration.questionnaires.subtitle')}
				onBack={() =>
					navigate(adminBackRoute(ROUTES.ADMINISTRATION.QUESTIONNAIRES))
				}
				footerText={`${filtered.length} ${
					filtered.length === 1
						? t('administration.questionnaires.countSingular')
						: t('administration.questionnaires.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.questionnaires.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
						dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-lg"
					>
						<QuestionnaireFormContent
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							branches={branchOptions}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.questionnaires.searchPlaceholder')}
				/>
				{isLoading ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<ClipboardList className="size-8 text-kaefer-light-silver" />
						<p className="text-sm">Carregando...</p>
					</div>
				) : (
					<>
						{/* Questionnaire list */}
						{filtered.length === 0 ? (
							<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
								<ClipboardList className="size-8 text-kaefer-light-silver" />
								<p className="text-sm">
									{t('administration.questionnaires.noResults')}
								</p>
							</div>
						) : (
							<div className="space-y-2">
								{filtered.map((q) => (
									<div
										key={q.id}
										className="relative overflow-hidden rounded-lg border border-kaefer-light-silver bg-white"
									>
										<div className="relative flex items-center px-4 py-3">
											{/* Clickable area */}
											<div
												className="flex flex-1 cursor-pointer items-center gap-3 pr-20"
												onClick={() => handleOpenEdit(q)}
											>
												<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-kaefer-medium-grey/10 text-sx font-semibold tabular-nums text-kaefer-medium-grey">
													{q.number}
												</span>
												<div className="min-w-0 flex-1">
													<span className=" text-kaefer-dark-grey">
														{q.title}
													</span>
													{q.kind === 'FREE_TEXT' && (
														<span className="ml-2 inline-flex items-center rounded-full bg-kaefer-violet/15 px-2 py-0.5 text-[11px] font-semibold text-kaefer-violet">
															{t('administration.questionnaires.freeTextBadge')}
														</span>
													)}
												</div>
												<span className="hidden items-center justify-center rounded-full bg-kaefer-medium-grey/15 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey sm:inline-flex">
													{t('administration.questionnaires.questionCount', {
														count: q.questions.length,
													})}
												</span>
											</div>

											{/* Action buttons */}
											<div className="absolute inset-y-0 right-4 flex items-center gap-1">
												<Button
													type="button"
													variant="ghost"
													size="icon-circle"
													onClick={() => handleOpenEdit(q)}
													className="text-kaefer-dark-grey hover:bg-transparent hover:text-kaefer-dark-grey/70"
												>
													<Pencil />
												</Button>
												<Button
													type="button"
													variant="ghost"
													size="icon-circle"
													onClick={() =>
														setDeletingItem({ id: q.id, title: q.title })
													}
													className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
												>
													<Trash2 />
												</Button>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!deletingItem}
				onOpenChange={(open) => {
					if (!open) {
						setDeletingItem(null)
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
						<AlertDialogDescription>
							Deseja realmente deletar o questionário
							{deletingItem?.title ? ` "${deletingItem.title}"` : ''}?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDelete}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
